// Oscilloscope — draws an AnalyserNode's time-domain buffer onto a canvas.
//
// Not self-animating: a single shared rAF loop (Phase 11) calls draw() on all
// three scopes. A rising zero-crossing trigger keeps the trace stable instead
// of scrolling. Construct with the canvas; attach the analyser once audio runs.

export class Oscilloscope {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly canvas: HTMLCanvasElement;
  private analyser: AnalyserNode | null = null;
  private buf: Uint8Array<ArrayBuffer> = new Uint8Array(0);

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;
  }

  setAnalyser(analyser: AnalyserNode | null): void {
    this.analyser = analyser;
    if (analyser) this.buf = new Uint8Array(analyser.fftSize);
  }

  draw(): void {
    const { ctx, canvas } = this;
    const w = canvas.width;
    const h = canvas.height;

    ctx.clearRect(0, 0, w, h);
    // Centre line.
    ctx.strokeStyle = 'rgba(216, 66, 31, 0.25)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, h / 2);
    ctx.lineTo(w, h / 2);
    ctx.stroke();

    if (!this.analyser) return;
    this.analyser.getByteTimeDomainData(this.buf);
    const n = this.buf.length;

    // Trigger: first rising crossing of the midpoint in the first quarter.
    let start = 0;
    for (let i = 1; i < n >> 2; i++) {
      if (this.buf[i - 1]! < 128 && this.buf[i]! >= 128) {
        start = i;
        break;
      }
    }

    // Draw half the buffer from the trigger so the window is stable.
    const span = n >> 1;
    ctx.strokeStyle = '#e8852a';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let i = 0; i < span; i++) {
      const v = this.buf[Math.min(n - 1, start + i)]! / 128 - 1; // -1..1
      const x = (i / (span - 1)) * w;
      const y = h / 2 - v * (h / 2 - 2);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
}
